import { useEffect } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { apiFetch } from "@pontimed/api-client";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

import { Skeleton } from "@/components/ui/skeleton";
import { useDefaultCountry } from "@/lib/hooks/use-default-country";
import { LEGACY_ACCOUNT_PREFIX } from "@/lib/account-path";

interface ResolveResponse {
  path: string;
}

export const Route = createFileRoute("/_authenticated/account/")({
  component: AccountRootPage,
});

/**
 * Account root — resolves the user's preferred workspace and redirects.
 *
 * Apps/api `/api/account/resolve-workspace` does the DB lookup +
 * persists `lastWorkspaceType/Id` in the session, returning a path like
 * `/account/user/dashboard` or `/account/provider/dashboard`. We strip
 * the legacy `/<locale>/<country>` prefix the endpoint emits (apps/app
 * routes are unprefixed) before navigating.
 */
function AccountRootPage() {
  const navigate = useNavigate();
  const { i18n } = useLingui();
  // Handler requires locale+country (legacy passed the URL params); they only
  // shape the `/<locale>/<country>` prefix we strip below. Lowercase so the
  // strip regex matches.
  const locale = i18n.locale;
  const { country } = useDefaultCountry();
  const countryLower = country.toLowerCase();
  const query = useQuery({
    queryKey: ["api", "/api/account/resolve-workspace", locale, countryLower],
    queryFn: () =>
      apiFetch.get<ResolveResponse>(
        `/api/account/resolve-workspace?locale=${encodeURIComponent(locale)}&country=${encodeURIComponent(countryLower)}`,
      ),
    staleTime: 0,
    gcTime: 0,
    retry: 1,
  });

  useEffect(() => {
    if (!query.data?.path) return;
    // The API returns a bare `/account/…` path; older API versions prefixed
    // it with `/<locale>/<country>` ("/ro/ro/account/user/dashboard").
    const stripped = query.data.path.replace(LEGACY_ACCOUNT_PREFIX, "");
    navigate({ to: stripped as never, replace: true });
  }, [query.data, navigate]);

  useEffect(() => {
    if (query.isError) navigate({ to: "/", replace: true });
  }, [query.isError, navigate]);

  return (
    <div className="space-y-4 p-4">
      <Skeleton className="h-8 w-40" />
      <Skeleton className="h-32 w-full rounded-xl" />
      <p className="text-muted-foreground text-center text-sm">
        <Trans>Loading your workspace…</Trans>
      </p>
    </div>
  );
}
